'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Thermometer,
  Wind,
  Sun,
  Cloudy,
  CloudSun,
  CloudRain,
  Loader2,
} from 'lucide-react';
import {
  liveWeatherSummary,
  LiveWeatherSummaryOutput,
} from '@/ai/flows/live-weather-summary';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

function getWeatherIcon(summary: string, isRaining: boolean, className: string = 'w-4 h-4'): React.ReactNode {
  const lowerCaseSummary = summary.toLowerCase();

  if (isRaining) {
    return <CloudRain className={className} />;
  }
  if (
    lowerCaseSummary.includes('cloudy') ||
    lowerCaseSummary.includes('overcast')
  ) {
    if (
      lowerCaseSummary.includes('partly') ||
      lowerCaseSummary.includes('few') ||
      lowerCaseSummary.includes('scattered')
    ) {
      return <CloudSun className={className} />;
    }
    return <Cloudy className={className} />;
  }
  if (lowerCaseSummary.includes('clear') || lowerCaseSummary.includes('sunny')) {
    return <Sun className={className} />;
  }

  return <Thermometer className={className} />;
}

/**
 * Compact weather indicator for the navigation bar
 * Shows current temperature, conditions icon, and wind speed
 */
export function WeatherIndicator() {
  const [weather, setWeather] = useState<LiveWeatherSummaryOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function fetchWeather() {
      try {
        setLoading(true);
        setError(false);
        const result = await liveWeatherSummary({});
        setWeather(result);
      } catch (e) {
        console.error('Weather indicator error:', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchWeather();

    // Refresh weather every 10 minutes
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-1 text-muted-foreground px-2">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error || !weather) {
    return null; // Don't show anything if weather fails to load
  }

  const WeatherIcon = getWeatherIcon(weather.summary, weather.rain);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            href="/#weather"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
          >
            <span className="text-primary">{WeatherIcon}</span>
            <span className="font-medium">{weather.temperature}°F</span>
            <span className="hidden sm:flex items-center gap-1 text-xs">
              <Wind className="w-3 h-3" />
              {weather.windSpeed}
            </span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <div className="space-y-1">
            <p className="font-medium">{weather.summary}</p>
            <p>Wind: {weather.windSpeed} mph {weather.windDirection}</p>
            <p>Humidity: {weather.humidity}%</p>
            {weather.rain && <p className="text-blue-500">Currently raining</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
